/* Movatic WMS — Frontend | CesarFilho 932048016 */
import { useEffect, useRef, useState } from 'react'
import { Cpu, AlertTriangle, RefreshCw } from 'lucide-react'

/**
 * Página genérica que embebe uma UI externa dentro da casca do WMS por <iframe>
 * (ADR-0052). UM só componente reutilizado por vários módulos (MFC, Catálogo
 * Global, …): recebe o `src` COMPLETO (já em modo-embed) e um `title` acessível.
 * Estados de loading e de erro/fallback sóbrio (serviço em baixo → mensagem,
 * nunca ecrã branco). O iframe é conteúdo isolado (sandbox); não toca na
 * autenticação do WMS.
 */

// Se o iframe não sinalizar load neste tempo, assumimos que o serviço está indisponível.
const LOAD_TIMEOUT_MS = 12000

type Props = {
  /** URL completa do iframe (já com ?embed=1 quando aplicável). */
  src: string
  /** Título acessível do iframe + rótulo usado nos estados de loading/erro. */
  title: string
}

type FrameState = 'loading' | 'ready' | 'error'

export default function EmbedFrame({ src, title }: Props) {
  const [state, setState] = useState<FrameState>('loading')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  // Ao trocar de destino (src muda), volta ao estado de loading.
  useEffect(() => {
    setState('loading')
  }, [src])

  // Timeout de segurança: se não carregar a tempo, mostra o fallback.
  useEffect(() => {
    if (state !== 'loading') return
    const timer = window.setTimeout(() => {
      setState((s) => (s === 'loading' ? 'error' : s))
    }, LOAD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [state, src])

  const reload = () => {
    setState('loading')
    // Reatribuir o src força o browser a recarregar o iframe.
    if (iframeRef.current) iframeRef.current.src = src
  }

  return (
    <div className="relative h-[calc(100vh-140px)] min-h-[480px] w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-inner">
      {state === 'loading' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900 text-slate-400">
          <Cpu size={28} className="animate-pulse" />
          <p className="text-sm">A carregar {title}…</p>
        </div>
      )}

      {state === 'error' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900 px-6 text-center text-slate-300">
          <AlertTriangle size={30} className="text-amber-400" />
          <p className="text-base font-semibold">{title} indisponível</p>
          <p className="max-w-md text-sm text-slate-400">
            Não foi possível carregar {title}. O serviço pode estar em baixo ou
            inacessível. Tente novamente dentro de momentos.
          </p>
          <button
            type="button"
            onClick={reload}
            className="mt-1 inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-200 transition hover:bg-slate-800"
          >
            <RefreshCw size={15} /> Tentar novamente
          </button>
        </div>
      )}

      <iframe
        ref={iframeRef}
        key={src}
        src={src}
        title={title}
        className="h-full w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        onLoad={() => setState('ready')}
        onError={() => setState('error')}
      />
    </div>
  )
}
